import type { Component } from "vue";
import type { DebugSection, DebugSectionIcon } from "./types";
import { defineAsyncComponent, markRaw } from "vue";

type SectionLoader = () => Promise<Component | { default: Component }>;

/** A component object (an SFC) or a loader. A function is always treated as a loader, never as a functional component. */
type SectionComponent = Exclude<Component, (...args: any[]) => any> | SectionLoader;

export interface DebugSectionDefinition {
  id: string
  /** Translation key of the rail label. */
  label: string
  icon: DebugSectionIcon
  /** The section body, or a loader such as `() => import("./MySection.vue")` so it only loads when opened. */
  component: SectionComponent
  footer?: "close" | SectionComponent
  order?: number
}

function resolveComponent(component: SectionComponent): Component {
  if (typeof component === "function") {
    return markRaw(defineAsyncComponent(component as SectionLoader));
  }
  return markRaw(component);
}

/** Describes one tab of the debug tools. Loaders become async components, so a section loads when it is opened. */
export function defineDebugSection(definition: DebugSectionDefinition): DebugSection {
  if (definition.id.trim() === "") {
    throw new Error("A debug section needs an id.");
  }

  return markRaw({
    id: definition.id,
    label: definition.label,
    icon: definition.icon,
    component: resolveComponent(definition.component),
    footer: definition.footer === undefined || definition.footer === "close"
      ? "close"
      : resolveComponent(definition.footer),
    order: definition.order,
  });
}

/** Rail order: by `order`, sections without one after those with one, equal orders in registration order. */
export function sortDebugSections(sections: readonly DebugSection[]): DebugSection[] {
  return sections
    .map((section, index) => ({ section, index }))
    .sort((a, b) => {
      const orderA = a.section.order ?? Number.POSITIVE_INFINITY;
      const orderB = b.section.order ?? Number.POSITIVE_INFINITY;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.index - b.index;
    })
    .map(({ section }) => section);
}
